<template>
  <div
    class="cycle-settings-threshold"
    data-testid="threshold-new"
  >
    <div v-if="loading && !formatedDataCopy.length" data-testid="threshold-new__loading">
      <v-skeleton-loader type="table-heading, list-item-two-line, article" data-testid="threshold-new__loading-skeleton" />
    </div>

    <template v-else>
      <div class="cycle-settings-threshold__heading" data-testid="threshold-new__action-bar">
        <h5 class="cycle-settings-threshold__description noTransform" data-testid="threshold-new__description">
          Define and manage thresholds by location to control reconciliation tolerance limits.
        </h5>
        <div class="cycle-settings-threshold__actions" data-testid="threshold-new__actions">
          <v-btn
            v-if="hasFilterActive"
            text
            class="cycle-settings-threshold__clear-filter"
            data-testid="threshold-new__clear-filter-btn"
            @click="clearTableFilters"
          >
            <span>Clear Filter</span>
          </v-btn>
          <v-btn
            v-if="canEditThreshold"
            class="cycle-settings-threshold__set-btn"
            data-testid="threshold-new__add-btn"
            @click="openAddDrawer"
          >
            <span data-testid="threshold-new__add-label">Set threshold</span>
          </v-btn>
          <div v-if="canEditThreshold" class="cycle-settings-threshold__divider" aria-hidden="true" />
          <div class="cycle-settings-threshold__activity" data-testid="threshold-new__activitylog-wrap">
            <activity-log
              :resource-id="activityResourceId"
              row-description="Threshold"
              :type="ACTIVITY_LOG_TYPES.CYCLE_THRESHOLD"
              data-testid="threshold-new__activity-log-btn"
            />
          </div>
        </div>
      </div>

      <lb-datatableaction
        ref="dataTableAction"
        :headers="headers"
        :tabledata="formatedDataCopy"
        :displayed-row-count="displayRows.length"
        :itemsperpage="itemsPerPage"
        :bulk-actions="false"
        :show-toolbar="false"
        :selectfilter="activeColumnFilters"
        :auto-height="false"
        data-testid="threshold-new__datatable"
        @filter-item="applyColumnFilters"
        @clear-item="clearColumnFilters"
        @filter-active-change="onFilterActiveChange"
      >
        <template v-slot:tbody>
          <tr
            v-for="item in displayRows"
            :key="item._id"
            class="cycle-settings-threshold__row"
            :class="{ 'cycle-settings-threshold__row--disabled': item.status === false }"
            data-testid="threshold-new__row"
          >
            <template v-for="header in headers">
              <td
                v-if="header.visible && header.value !== 'action'"
                :key="header.value"
                class="px-4 single-line cycle-settings-threshold__td"
                :class="header.cellClass"
                data-testid="threshold-new__cell"
              >
                <span
                  v-if="header.value === 'name'"
                  class="cycle-settings-threshold__location-text"
                >
                  {{ item.name }}
                </span>
                <span
                  v-else-if="header.value === 'threshold'"
                  class="cycle-settings-threshold__value-text"
                >
                  {{ item.threshold }}
                </span>
                <span v-else data-testid="threshold-new__cell-value">{{ getItemData(item, header.value) }}</span>
              </td>
              <td
                v-else-if="header.visible && header.value === 'action'"
                :key="`action-${header.value}`"
                class="px-4 single-line action-data cycle-settings-threshold__td cycle-settings-threshold__td--action"
                :class="header.cellClass"
                data-testid="threshold-new__row-actions"
              >
                <div class="cycle-settings-threshold__action-group">
                  <v-tooltip v-if="canEditThreshold" bottom content-class="tooltip-bottom">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="cycle-settings-threshold__icon-btn"
                        v-bind="attrs"
                        v-on="on"
                        data-testid="threshold-new__edit-btn"
                        @click.stop="openEditDrawer(item)"
                      >
                        <img
                          class="cycle-settings-threshold__icon"
                          src="/img/cycle-settings/edit.svg"
                          alt="Edit"
                          data-testid="threshold-new__edit-icon"
                        />
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>

                  <v-tooltip bottom content-class="tooltip-bottom">
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <v-switch
                          :key="statusSwitchKey(item)"
                          class="cycle-settings-threshold__switch"
                          dense
                          inset
                          hide-details
                          :disabled="!canEditThreshold || !!statusUpdatePending[item._id]"
                          :input-value="!!item.status"
                          :data-testid="item.status ? 'threshold-new__status-btn-enabled' : 'threshold-new__status-btn-disabled'"
                          @change="onStatusSwitchChange(item, $event)"
                        />
                      </div>
                    </template>
                    <span>{{ item.status ? 'Disable' : 'Enable' }}</span>
                  </v-tooltip>
                </div>
              </td>
            </template>
          </tr>
        </template>
      </lb-datatableaction>
    </template>

    <div class="cycle-settings-threshold__pagination" data-testid="threshold-new__pagination-wrap">
      <Pagination
        :list="formatedData"
        :itemsperpage="itemsPerPage"
        :inline="true"
        data-testid="threshold-new__pagination"
        @paginated-data="setPaginatedData"
      />
    </div>

    <threshold-form-drawer
      :visible.sync="drawerVisible"
      :loading="drawerLoading"
      :location-header-label="locationHeaderLabel"
      :location-list="locationList"
      :form.sync="drawerForm"
      :errors="drawerErrors"
      :enabled.sync="enableSwitch"
      @submit="onDrawerSubmit"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Pagination from '../../components/Pagination/Pagination.vue';
import ActivityLog from '../../components/ActivityLog/ActivityLog.vue';
import ThresholdFormDrawer from './CycleSettings/components/ThresholdFormDrawer.vue';
import {
  formatThresholdRows,
  getEligibleLocations,
  parseThresholdValue,
  validateProjectThresholdData
} from '../../utils/cycleSettingsUtils';
import { ACTIVITY_LOG_TYPES } from '../../utils/constants';

export default {
  name: 'CycleSettingsThreshold',
  components: {
    Pagination,
    ActivityLog,
    ThresholdFormDrawer
  },
  data() {
    return {
      right: 'financialclose_project',
      drawerVisible: false,
      drawerLoading: false,
      drawerForm: {},
      drawerErrors: {},
      loading: false,
      projectId: '',
      listdata: [],
      locationListdata: [],
      formatedData: [],
      formatedDataCopy: [],
      locationList: [],
      metadata: [],
      enableSwitch: true,
      itemsPerPage: 16,
      paginatedItems: [],
      activeColumnFilters: {},
      hasFilterActive: false,
      statusSwitchNonce: {},
      statusUpdatePending: {},
      canEditCycleThreshold: false
    };
  },
  computed: {
    ACTIVITY_LOG_TYPES() {
      return ACTIVITY_LOG_TYPES;
    },
    canEditThreshold() {
      return this.canEditCycleThreshold === true;
    },
    locationHeaderLabel() {
      return this.$store.state.LocationHeader || 'Location';
    },
    activityResourceId() {
      return (this.metadata && this.metadata._id) || this.projectId || '';
    },
    displayRows() {
      return (this.paginatedItems && this.paginatedItems.length)
        ? this.paginatedItems
        : this.formatedData;
    },
    filteredLocationsFromStore() {
      return this.$store.getters['tasks/getFilteredLocations'] || [];
    },
    headers() {
      return [
        {
          value: 'name',
          text: this.locationHeaderLabel,
          sortable: false,
          filterable: true,
          visible: true,
          headerClass: 'cycle-settings-threshold__col--location',
          cellClass: 'cycle-settings-threshold__col--location'
        },
        {
          value: 'threshold',
          text: 'Threshold',
          sortable: false,
          filterable: true,
          visible: true,
          headerClass: 'cycle-settings-threshold__col--value',
          cellClass: 'cycle-settings-threshold__col--value'
        },
        {
          value: 'action',
          text: 'Action',
          sortable: false,
          filterable: false,
          visible: true,
          headerClass: 'cycle-settings-threshold__col--action',
          cellClass: 'cycle-settings-threshold__col--action'
        }
      ];
    }
  },
  created() {
    this.$store.dispatch('tasks/ensureFilteredLocations', { cycleId: this.$route.params.id });
    this.refreshData();
  },
  activated() {
    this.refreshData();
  },
  watch: {
    drawerVisible(visible) {
      if (!visible) {
        this.drawerForm = {};
        this.drawerErrors = {};
      }
    },
    formatedDataCopy: {
      handler(newList) {
        this.applyColumnFilters(this.activeColumnFilters, newList);
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('project', [
      'fetchCycleThresholds',
      'addCycleThreshold',
      'updateCycleThreshold'
    ]),
    setPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedItems = paginationInfo.data || [];
      } else {
        this.paginatedItems = paginationInfo || [];
      }
    },
    applyColumnFilters(item, sourceList) {
      const source = Array.isArray(sourceList) ? sourceList : this.formatedDataCopy;
      this.activeColumnFilters = item || {};
      const hasFilters = Object.keys(this.activeColumnFilters).length > 0;
      if (hasFilters && this.$nova?.filterByDynamicCriteria) {
        this.formatedData = this.$nova.filterByDynamicCriteria(source, this.activeColumnFilters);
      } else {
        this.formatedData = source.slice();
      }
    },
    clearColumnFilters() {
      this.activeColumnFilters = {};
      this.hasFilterActive = false;
      this.formatedData = this.formatedDataCopy.slice();
    },
    onFilterActiveChange(isActive) {
      this.hasFilterActive = !!isActive;
    },
    clearTableFilters() {
      this.$refs.dataTableAction?.clearFilter?.();
    },
    getItemData(item, key) {
      return item?.[key];
    },
    refreshData() {
      this.projectId = this.$route.params.id || '';
      this.getData();
    },
    syncFormattedData() {
      this.formatedData = formatThresholdRows(this.listdata, this.locationListdata);
      this.formatedDataCopy = this.formatedData.slice();
      this.locationList = getEligibleLocations(this.locationListdata, this.listdata);
    },
    openAddDrawer() {
      this.locationList = getEligibleLocations(this.locationListdata, this.listdata);
      if (this.loading || !this.locationList.length) {
        this.$store.commit('sbInfo', 'All locations already have thresholds configured.');
        return;
      }
      this.drawerErrors = {};
      this.enableSwitch = true;
      // Reset first so lb-dropdown re-syncs when the same location is chosen again after create/edit.
      this.drawerForm = {};
      this.$nextTick(() => {
        this.drawerForm = { location: '', threshold: '' };
        this.drawerVisible = true;
      });
    },
    openEditDrawer(item) {
      const thresholdValue = parseThresholdValue(item.threshold);
      const selectedLocationId = item.location || item.locationId || this.getLocationIdFromName(item.name);
      this.locationList = getEligibleLocations(this.locationListdata, this.listdata, selectedLocationId);
      this.enableSwitch = item.status;
      this.drawerErrors = {};
      // Clear form before assigning so location value always triggers lb-dropdown's value watcher.
      // After create, drawerForm.location often still holds the same id while locationList had
      // temporarily dropped that item (items watcher cleared internal content) — without a reset,
      // edit opens with an empty location display.
      this.drawerForm = {};
      this.$nextTick(() => {
        this.drawerForm = {
          _id: item._id,
          location: selectedLocationId,
          threshold: thresholdValue,
          status: item.status,
          name: item.name
        };
        this.drawerVisible = true;
      });
    },
    getLocationIdFromName(name) {
      if (!name) return '';
      const match = (this.locationListdata || []).find((item) => item.name === name);
      return match?._id || '';
    },
    async getData() {
      this.loading = true;
      try {
        const result = await this.fetchCycleThresholds({
          cycleId: this.projectId,
          filteredLocations: this.filteredLocationsFromStore
        });
        this.listdata = result.listdata || [];
        this.metadata = result.metadata || {};
        this.locationListdata = result.locationListdata || [];
        this.canEditCycleThreshold = result.canEditThreshold === true;
        this.syncFormattedData();
      } catch (err) {
        console.error('[CycleSettingsThreshold]: getData error', err);
      } finally {
        this.loading = false;
      }
    },
    async onDrawerSubmit() {
      const threshold = parseThresholdValue(this.drawerForm.threshold);
      const formData = { ...this.drawerForm, threshold };
      const errors = validateProjectThresholdData(formData);
      this.drawerErrors = errors;
      if (Object.keys(errors).length > 0) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.');
        return;
      }

      this.drawerLoading = true;
      const payload = {
        location: formData.location,
        threshold,
        status: this.enableSwitch,
      };
      if (formData._id) {
        payload._id = formData._id;
      }
      try {
        let result;
        if (this.drawerForm._id) {
          result = await this.updateCycleThreshold({
            thresholdId: this.drawerForm._id,
            data: payload
          });
        } else {
          result = await this.addCycleThreshold({
            cycleId: this.projectId,
            data: payload
          });
        }
        if (result?.success) {
          this.drawerVisible = false;
          this.drawerForm = {};
          await this.getData();
        } else {
          if (result?.fieldErrors) {
            this.drawerErrors = result.fieldErrors;
          }
          if (result?.message) {
            this.$store.commit('sbError', result.message);
          }
        }
      } catch (err) {
        console.error('[CycleSettingsThreshold]: onDrawerSubmit error', err);
      } finally {
        this.drawerLoading = false;
      }
    },
    statusSwitchKey(item) {
      const id = item && item._id;
      return `threshold-status-${id}-${this.statusSwitchNonce[id] || 0}`;
    },
    revertStatusSwitch(id) {
      this.$set(this.statusSwitchNonce, id, (this.statusSwitchNonce[id] || 0) + 1);
    },
    async onStatusSwitchChange(item, newStatus) {
      const requested = !!newStatus;
      if (!item || requested === !!item.status || this.statusUpdatePending[item._id]) return;
      this.$set(this.statusUpdatePending, item._id, true);
      try {
        // v-switch flips its own state before this handler. Put it back until the API agrees.
        await this.$nextTick();
        this.revertStatusSwitch(item._id);
        await this.updateThresholdStatus({
          ...item,
          threshold: parseThresholdValue(item.threshold),
          status: requested
        });
      } finally {
        this.$delete(this.statusUpdatePending, item._id);
      }
    },
    async updateThresholdStatus(item) {
      try {
        const result = await this.updateCycleThreshold({
          thresholdId: item._id,
          data: item
        });
        if (result?.success) {
          await this.getData();
          return;
        }
        this.revertStatusSwitch(item._id);
        if (result?.fieldErrors) {
          this.drawerErrors = result.fieldErrors;
        }
        if (result?.message) {
          this.$store.commit('sbError', result.message);
        }
      } catch (err) {
        console.error('[CycleSettingsThreshold]: updateThresholdStatus error', err);
        this.revertStatusSwitch(item._id);
      }
    }
  }
};
</script>

<style lang="scss">
@import './CycleSettings/CycleSettingsThreshold.scss';
</style>
